import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API } from "@/config";
import { GET_ERP_PRODUCTS_FETCH_KEY } from "./useGetErpProducts";
import { fetcher } from "@/shared/core-configs";

export const ERP_SYNC_JOB_FETCH_KEY = "erpSyncJob";

export type ErpSyncStatus = "PENDING" | "RUNNING" | "DONE" | "ERROR";

export interface ErpSyncJob {
  id: string;
  zoneId: string;
  status: ErpSyncStatus;
  createdCount: number | null;
  updatedCount: number | null;
  syncedCount: number | null;
  error: string | null;
}

const POLL_INTERVAL_MS = 3000;

export const requestErpSync = async (zoneId: string): Promise<ErpSyncJob> => {
  const response = await fetcher.post(`${API.GESLUB}/erp-product/sync-erp`, {
    zoneId,
  });
  return response.data;
};

export const getErpSyncJob = async (id: string): Promise<ErpSyncJob> => {
  const response = await fetcher.get(
    `${API.GESLUB}/erp-product/sync-erp/${id}`,
  );
  return response.data;
};

export const useForceErpSync = ({
  onDone,
  onError,
}: {
  onDone?: (job: ErpSyncJob) => void;
  onError?: (message: string) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: requestErpSync,
    onSuccess: (job) => setJobId(job.id),
  });

  const { data: job } = useQuery({
    queryKey: [ERP_SYNC_JOB_FETCH_KEY, jobId],
    queryFn: () => getErpSyncJob(jobId as string),
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "DONE" || status === "ERROR" ? false : POLL_INTERVAL_MS;
    },
  });

  useEffect(() => {
    if (!job) return;

    if (job.status === "DONE") {
      setJobId(null);
      queryClient.invalidateQueries({ queryKey: [GET_ERP_PRODUCTS_FETCH_KEY] });
      onDone?.(job);
    }

    if (job.status === "ERROR") {
      setJobId(null);
      onError?.(job.error ?? "La sincronizacion con Totvs fallo");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, queryClient]);

  const isWaiting = Boolean(jobId);

  return {
    start: mutation.mutate,
    isPending: mutation.isPending || isWaiting,
    status: job?.status,
    error: mutation.error,
    isError: mutation.isError,
  };
};
